import waveMarkup from '@sts/components/ui/Card/assets/wave.svg?raw';

type RestApiCard = {
    id?: number;
    title?: string;
    excerpt?: string;
    link?: string;
    type?: string;
    image?: { url?: string | null; alt?: string | null } | string | null;
};

type TagData = {
    id: number;
    name: string;
    slug: string;
};

type FilterPostsResponse = {
    items: RestApiCard[];
    tags: TagData[];
    page: number;
    total: number;
    total_pages: number;
    has_more: boolean;
    post_type: string;
    hashtag?: string | null;
};

type FilterState = {
    page: number;
    hashtag: string;
};

type QBankVariant = '16-9' | '1-1' | '9-16';
type QBankSize = 'thumb' | 'xsmall' | 'small' | 'medium' | 'large';
type QBankMeta = {
    hash: string;
    baseUrl: string;
};

const PAGE_PARAM = 'fp';
const DEFAULT_VISIBLE_TAGS = 10;
const QBANK_HOST = 'powered-by.qbank.se';
const QBANK_VARIANT_SIZES: Record<QBankVariant, QBankSize[]> = {
    '16-9': ['small', 'medium', 'large'],
    '1-1': ['thumb', 'xsmall', 'small', 'medium', 'large'],
    '9-16': ['small', 'medium', 'large'],
};
const QBANK_SIZE_MAP: Record<QBankSize, number> = {
    thumb: 150,
    xsmall: 500,
    small: 800,
    medium: 1024,
    large: 1536,
};

const SELECTORS = {
    block: '.wp-block-sts-filter-posts-news',
    tags: '[data-filter-tags]',
    list: '[data-filter-list]',
    empty: '[data-filter-empty]',
    more: '[data-filter-more]',
    loading: '[data-filter-loading]',
};

const getUrlParams = () => new URLSearchParams(window.location.search);

const normalizePage = (value?: string | null): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
};

const normalizeHashtag = (value?: string | null): string =>
    typeof value === 'string' ? value.trim() : '';

const normalizeLanguage = (value?: string | null): string =>
    typeof value === 'string' ? value.trim().toLowerCase().replace(/_/g, '-') : '';

const parseQBankMeta = (value: string): QBankMeta | null => {
    const normalizedValue = value.trim();
    if (!normalizedValue) {
        return null;
    }

    let parsed: URL;

    try {
        parsed = new URL(normalizedValue, window.location.origin);
    } catch {
        return null;
    }

    if (parsed.hostname.toLowerCase() !== QBANK_HOST) {
        return null;
    }

    const pathname = parsed.pathname || '';
    if (!pathname.toLowerCase().endsWith('.webp')) {
        return null;
    }

    const fileName = pathname.split('/').pop() || '';
    const baseName = fileName.replace(/\.webp$/i, '');
    if (!baseName) {
        return null;
    }

    const hash = (baseName.split('_')[0] || '').trim();
    if (!hash) {
        return null;
    }

    let basePath = pathname.slice(0, Math.max(0, pathname.length - fileName.length));
    basePath = basePath.replace(/\/+/g, '/');

    if (!basePath) {
        basePath = '/';
    }

    if (!basePath.endsWith('/')) {
        basePath += '/';
    }

    return {
        hash,
        baseUrl: `${parsed.protocol}//${parsed.host}${basePath}`,
    };
};

const buildQBankSrcSet = (baseUrl: string, hash: string, variant: QBankVariant): string => {
    const sizes = QBANK_VARIANT_SIZES[variant] || QBANK_VARIANT_SIZES['1-1'];

    return sizes
        .map((size) => {
            const baseWidth = QBANK_SIZE_MAP[size];
            const width = variant === '9-16' ? Math.round((baseWidth * 9) / 16) : baseWidth;
            return `${baseUrl}${hash}_${variant}-${size}.webp ${width}w`;
        })
        .join(', ');
};

const createCardImageElement = (
    imageUrl: string,
    imageAlt: string,
    doc: Document
): HTMLElement => {
    const qbankMeta = parseQBankMeta(imageUrl);

    if (!qbankMeta) {
        const img = doc.createElement('img');
        img.src = imageUrl;
        img.alt = imageAlt;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.setAttribute('fetchpriority', 'low');
        return img;
    }

    const srcSet = buildQBankSrcSet(qbankMeta.baseUrl, qbankMeta.hash, '1-1');
    const fallback = `${qbankMeta.baseUrl}${qbankMeta.hash}_1-1-thumb.webp`;

    const picture = doc.createElement('picture');
    picture.className = 'sts-responsive-image has-aspect-square has-fit-cover';

    const mobileSource = doc.createElement('source');
    mobileSource.setAttribute('media', '(max-width: 1023.98px)');
    mobileSource.setAttribute('srcset', srcSet);
    mobileSource.setAttribute('type', 'image/webp');

    const desktopSource = doc.createElement('source');
    desktopSource.setAttribute('media', '(min-width: 1024px)');
    desktopSource.setAttribute('srcset', srcSet);
    desktopSource.setAttribute('type', 'image/webp');

    const img = doc.createElement('img');
    img.src = fallback;
    img.alt = imageAlt;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.setAttribute('fetchpriority', 'low');

    picture.appendChild(mobileSource);
    picture.appendChild(desktopSource);
    picture.appendChild(img);

    return picture;
};

const resolveCurrentLanguage = (): string =>
    normalizeLanguage((window as { STSBlocksData?: { currentLang?: string | null } })?.STSBlocksData?.currentLang);

const updateUrl = (state: FilterState) => {
    const params = getUrlParams();

    if (state.page > 1) {
        params.set(PAGE_PARAM, String(state.page));
    } else {
        params.delete(PAGE_PARAM);
    }

    if (state.hashtag) {
        params.set('hashtag', state.hashtag);
    } else {
        params.delete('hashtag');
    }

    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
    window.history.replaceState(null, '', nextUrl);
};

const ensureString = (value?: unknown, fallback = ''): string =>
    typeof value === 'string' ? value : fallback;

const normalizeApiImage = (
    image: RestApiCard['image']
): { url?: string | null; alt?: string | null } | null => {
    if (!image) {
        return null;
    }

    if (typeof image === 'string') {
        return { url: image };
    }

    if (typeof image === 'object') {
        return {
            url: 'url' in image ? (image.url as string | null) : undefined,
            alt: 'alt' in image ? (image.alt as string | null) : undefined,
        };
    }

    return null;
};

const buildCardFromApi = (card: RestApiCard) => {
    const id = Number(card?.id);
    if (!Number.isFinite(id) || id <= 0) {
        return null;
    }

    const title = ensureString(card?.title);
    const description = ensureString(card?.excerpt);
    const linkUrl = ensureString(card?.link);
    if (!linkUrl) {
        return null;
    }

    const image = normalizeApiImage(card?.image);

    return {
        id,
        title,
        description,
        link: { url: linkUrl, title: title || linkUrl, target: '_self' },
        image: image
            ? {
                  url: ensureString(image.url),
                  alt: ensureString(image.alt) || title,
              }
            : null,
    };
};

const createCardElement = (card: RestApiCard, doc: Document) => {
    const normalized = buildCardFromApi(card);

    if (!normalized || !normalized.link?.url) {
        return null;
    }

    const link = doc.createElement('a');
    link.className = 'sts-card sts-card-image';
    link.href = normalized.link.url;
    link.setAttribute('aria-label', normalized.title || normalized.link.title || '');
    if (normalized.link.target) {
        link.setAttribute('target', normalized.link.target);
        if (normalized.link.target === '_blank') {
            link.setAttribute('rel', 'noopener');
        }
    }

    if (normalized.image?.url) {
        const imageWrapper = doc.createElement('div');
        imageWrapper.className = 'sts-card-image__image';

        const imageElement = createCardImageElement(
            normalized.image.url,
            normalized.image.alt || normalized.title || '',
            doc
        );

        imageWrapper.appendChild(imageElement);

        link.appendChild(imageWrapper);
    }

    if (normalized.title || normalized.description) {
        const content = doc.createElement('div');
        content.className = 'sts-card__content';

        if (waveMarkup) {
            const wave = doc.createElement('div');
            wave.className = 'sts-card__content-wave';
            wave.setAttribute('aria-hidden', 'true');
            wave.innerHTML = waveMarkup;
            content.appendChild(wave);
        }

        if (normalized.title) {
            const heading = doc.createElement('h5');
            heading.textContent = normalized.title;
            content.appendChild(heading);
        }

        if (normalized.description) {
            const description = doc.createElement('p');
            description.textContent = normalized.description;
            content.appendChild(description);
        }

        link.appendChild(content);
    }

    return link;
};

const renderTags = (
    container: HTMLElement,
    tags: TagData[],
    activeSlug: string,
    isExpanded: boolean,
    readMoreLabel: string,
    onClick: (slug: string) => void,
    onExpand: () => void
) => {
    container.innerHTML = '';
    const shouldCollapse = tags.length > DEFAULT_VISIBLE_TAGS && !isExpanded;

    tags.forEach((tag, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sts-block-filter-posts__tag';
        if (tag.slug === activeSlug) {
            button.classList.add('is-active');
        }
        const shouldHide = shouldCollapse && index >= DEFAULT_VISIBLE_TAGS && tag.slug !== activeSlug;
        if (shouldHide) {
            button.hidden = true;
            button.dataset.filterTagExtra = 'true';
        }
        button.textContent = tag.name;
        button.addEventListener('click', () => onClick(tag.slug));
        container.appendChild(button);
    });

    if (shouldCollapse) {
        const readMoreButton = document.createElement('button');
        readMoreButton.type = 'button';
        readMoreButton.className =
            'sts-block-filter-posts__tag-more cta-link';
        readMoreButton.textContent = readMoreLabel;

        const icon = document.createElement('span');
        icon.className = 'material-symbols-outlined';
        icon.textContent = 'chevron_right';
        readMoreButton.appendChild(icon);
        readMoreButton.addEventListener('click', () => {
            container
                .querySelectorAll<HTMLButtonElement>('[data-filter-tag-extra="true"]')
                .forEach((button) => {
                    button.hidden = false;
                });
            readMoreButton.remove();
            onExpand();
        });

        container.appendChild(readMoreButton);
    }
};

const renderCards = (
    container: HTMLElement,
    cards: RestApiCard[],
    options?: { replace?: boolean }
) => {
    if (options?.replace) {
        container.innerHTML = '';
    }

    cards.forEach((card) => {
        const cardElement = createCardElement(card, container.ownerDocument);
        if (cardElement) {
            container.appendChild(cardElement);
        }
    });
};

const setEmptyState = (emptyEl: HTMLElement | null, isEmpty: boolean) => {
    if (!emptyEl) {
        return;
    }

    emptyEl.style.display = isEmpty ? '' : 'none';
};

const setMoreState = (button: HTMLButtonElement | null, isVisible: boolean) => {
    if (!button) {
        return;
    }

    button.style.display = isVisible ? '' : 'none';
};

const setLoadingState = (element: HTMLElement | null, isLoading: boolean) => {
    if (!element) {
        return;
    }

    element.classList.toggle('sts-block-filter-posts__posts--loading', isLoading);
};

const initBlock = (block: HTMLElement) => {
    const postType = block.dataset.postType || 'post';
    const readMoreLabel = block.dataset.readMoreLabel || 'Read more';
    const tagsContainer = block.querySelector<HTMLElement>(SELECTORS.tags);
    const listContainer = block.querySelector<HTMLElement>(SELECTORS.list);
    const emptyEl = block.querySelector<HTMLElement>(SELECTORS.empty);
    const moreButton = block.querySelector<HTMLButtonElement>(SELECTORS.more);
    const loadingEl = block.querySelector<HTMLElement>(SELECTORS.loading);

    if (!tagsContainer || !listContainer) {
        return;
    }

    const restRoot =
        (window as { wpApiSettings?: { root?: string } })?.wpApiSettings?.root ||
        `${window.location.origin}/wp-json/`;
    const restEndpoint = `${restRoot.replace(/\/?$/, '/')}` + 'sts/v1/filter-posts';

    const params = getUrlParams();
    const state: FilterState = {
        page: normalizePage(params.get(PAGE_PARAM)),
        hashtag: normalizeHashtag(params.get('hashtag')),
    };
    let isTagsExpanded = false;

    const fetchData = async (options?: { append?: boolean }) => {
        if (moreButton) {
            moreButton.disabled = true;
        }
        setLoadingState(loadingEl, true);

        const url = new URL(restEndpoint);
        url.searchParams.set('post_type', postType);
        url.searchParams.set('p', String(state.page));
        if (state.hashtag) {
            url.searchParams.set('hashtag', state.hashtag);
        }
        const currentLanguage = resolveCurrentLanguage();
        if (currentLanguage) {
            url.searchParams.set('lang', currentLanguage);
        }

        try {
            const response = await window.fetch(url.toString(), { credentials: 'same-origin' });
            if (!response.ok) {
                throw new Error('Failed to load posts');
            }
            const data = (await response.json()) as FilterPostsResponse;

            renderTags(
                tagsContainer,
                data.tags,
                state.hashtag,
                isTagsExpanded,
                readMoreLabel,
                (slug) => {
                    state.page = 1;
                    state.hashtag = state.hashtag === slug ? '' : slug;
                    updateUrl(state);
                    fetchData({ append: false });
                },
                () => {
                    isTagsExpanded = true;
                }
            );

            renderCards(listContainer, data.items, { replace: !options?.append });
            setEmptyState(emptyEl, data.items.length === 0 && !options?.append);
            setMoreState(moreButton, data.has_more);
        } catch (error) {
            setEmptyState(emptyEl, true);
            if (emptyEl) {
                emptyEl.textContent = 'Unable to load posts.';
            }
            setMoreState(moreButton, false);
        } finally {
            if (moreButton) {
                moreButton.disabled = false;
            }
            setLoadingState(loadingEl, false);
        }
    };

    if (moreButton) {
        moreButton.addEventListener('click', () => {
            state.page += 1;
            updateUrl(state);
            fetchData({ append: true });
        });
    }

    setLoadingState(loadingEl, true);
    updateUrl(state);
    fetchData({ append: false });
};

const init = () => {
    document.querySelectorAll<HTMLElement>(SELECTORS.block).forEach((block) => initBlock(block));
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
